import React, { useMemo, useState } from 'react';
import SortableTable from '../SortableTable';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import EolModal from '../ui/EolModal';

const DNControlEOL = ({ eolProducts, projectId, onRefresh }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'remove'
  const [selectedProductForModal, setSelectedProductForModal] = useState(null);
  // Préparer les colonnes pour le tableau
  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Nom du Produit',
      filterable: true,
      render: (item) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {item.name}
        </span>
      )
    },
    {
      key: 'universe',
      label: 'Univers',
      filterable: true,
      render: (item) => (
        <span className="text-gray-700 dark:text-gray-300">
          {item.universe || 'Non spécifié'}
        </span>
      )
    },
    {
      key: 'family',
      label: 'Famille',
      filterable: true,
      render: (item) => (
        <span className="text-gray-700 dark:text-gray-300">
          {item.family || 'Non spécifiée'}
        </span>
      )
    },
    {
      key: 'launched_at',
      label: 'Date de Lancement',
      filterable: false,
      render: (item) => {
        if (!item.launched_at) return <span className="text-gray-400">Non spécifiée</span>;

        try {
          const date = new Date(item.launched_at);
          return (
            <span className="text-gray-700 dark:text-gray-300">
              {format(date, 'dd/MM/yyyy', { locale: fr })}
            </span>
          );
        } catch (error) {
          return <span className="text-gray-400">Date invalide</span>;
        }
      },
      rawValue: (item) => item.launched_at,
      sortFunction: (a, b, direction) => {
        const aDate = a.launched_at ? new Date(a.launched_at) : new Date(0);
        const bDate = b.launched_at ? new Date(b.launched_at) : new Date(0);
        return direction === 'ascending' ? aDate - bDate : bDate - aDate;
      }
    },
    {
      key: 'terminated_at',
      label: 'Date de Fin de Vie',
      filterable: false,
      render: (item) => {
        if (!item.terminated_at) return <span className="text-gray-400">Non spécifiée</span>;

        try {
          const date = new Date(item.terminated_at);
          const today = new Date();
          const isRecentlyTerminated = (today - date) / (1000 * 60 * 60 * 24) <= 30; // 30 jours

          return (
            <div className="flex items-center">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {format(date, 'dd/MM/yyyy', { locale: fr })}
              </span>
              {isRecentlyTerminated && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                  Récent
                </span>
              )}
            </div>
          );
        } catch (error) {
          return <span className="text-gray-400">Date invalide</span>;
        }
      },
      rawValue: (item) => item.terminated_at,
      sortFunction: (a, b, direction) => {
        const aDate = a.terminated_at ? new Date(a.terminated_at) : new Date(0);
        const bDate = b.terminated_at ? new Date(b.terminated_at) : new Date(0);
        return direction === 'ascending' ? aDate - bDate : bDate - aDate;
      }
    },
    {
      key: 'days_since_eol',
      label: 'Jours depuis EOL',
      filterable: false,
      render: (item) => {
        if (!item.terminated_at) return <span className="text-gray-400">-</span>;

        try {
          const terminatedDate = new Date(item.terminated_at);
          const today = new Date();
          const daysSince = Math.floor((today - terminatedDate) / (1000 * 60 * 60 * 24));

          return (
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {daysSince} jour{daysSince > 1 ? 's' : ''}
            </span>
          );
        } catch (error) {
          return <span className="text-gray-400">-</span>;
        }
      },
      rawValue: (item) => {
        if (!item.terminated_at) return null;
        try {
          const terminatedDate = new Date(item.terminated_at);
          const today = new Date();
          return Math.floor((today - terminatedDate) / (1000 * 60 * 60 * 24));
        } catch (error) {
          return null;
        }
      },
      sortFunction: (a, b, direction) => {
        const aDays = a.terminated_at ? Math.floor((new Date() - new Date(a.terminated_at)) / (1000 * 60 * 60 * 24)) : null;
        const bDays = b.terminated_at ? Math.floor((new Date() - new Date(b.terminated_at)) / (1000 * 60 * 60 * 24)) : null;

        if (aDays === null && bDays === null) return 0;
        if (aDays === null) return direction === 'ascending' ? 1 : -1;
        if (bDays === null) return direction === 'ascending' ? -1 : 1;

        return direction === 'ascending' ? aDays - bDays : bDays - aDays;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      filterable: false,
      render: (item) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditProduct(item)}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            title="Modifier la date de fin de vie"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleRemoveProduct(item)}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            title="Retirer de la liste EOL"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )
    }
  ], []);

  // Calculer les statistiques
  const stats = useMemo(() => {
    if (!Array.isArray(eolProducts) || eolProducts.length === 0) {
      return {
        total: 0,
        recent: 0,
        thisMonth: 0,
        thisYear: 0
      };
    }

    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfYear = new Date(today.getFullYear(), 0, 1);

    return eolProducts.reduce((acc, product) => {
      if (product.terminated_at) {
        const terminatedDate = new Date(product.terminated_at);
        const daysSince = Math.floor((today - terminatedDate) / (1000 * 60 * 60 * 24));

        if (daysSince <= 30) acc.recent++;
        if (terminatedDate >= startOfMonth) acc.thisMonth++;
        if (terminatedDate >= startOfYear) acc.thisYear++;
      }
      return acc;
    }, {
      total: eolProducts.length,
      recent: 0,
      thisMonth: 0,
      thisYear: 0
    });
  }, [eolProducts]);

  // Fonctions pour gérer les actions du modal
  const handleAddProduct = () => {
    setModalMode('add');
    setSelectedProductForModal(null);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setModalMode('edit');
    setSelectedProductForModal(product);
    setIsModalOpen(true);
  };

  const handleRemoveProduct = (product) => {
    setModalMode('remove');
    setSelectedProductForModal(product);
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  if (!Array.isArray(eolProducts) || eolProducts.length === 0) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-blue-700 dark:text-blue-300">
            Aucun produit en fin de vie détecté pour la période sélectionnée.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-0">
      {/* En-tête avec bouton d'ajout */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Produits en Fin de Vie
        </h2>
        <button
          onClick={handleAddProduct}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Produit EOL
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {stats.total}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total produits EOL
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {stats.recent}
          </div>
          <div className="text-sm text-red-500 dark:text-red-400">
            EOL récents (≤30j)
          </div>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {stats.thisMonth}
          </div>
          <div className="text-sm text-orange-500 dark:text-orange-400">
            EOL ce mois-ci
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {stats.thisYear}
          </div>
          <div className="text-sm text-yellow-500 dark:text-yellow-400">
            EOL cette année
          </div>
        </div>
      </div>

      {/* Tableau des produits EOL */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <SortableTable
          data={eolProducts}
          columns={columns}
          emptyMessage="Aucun produit en fin de vie trouvé"
          countLabel={`${eolProducts.length} produit${eolProducts.length > 1 ? 's' : ''} en fin de vie`}
          exportable={true}
          exportFilename="produits-eol.csv"
          exportRenamable={true}
          globalSearch={true}
          initialSortBy={{ key: 'terminated_at', direction: 'descending' }}
          fullHeight={false}
        />
      </div>

      {/* Modal EOL réutilisable */}
      <EolModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projectId={projectId}
        onSuccess={handleModalSuccess}
        mode={modalMode}
        product={selectedProductForModal}
      />
    </div>
  );
};

// Composant Badge pour afficher le nombre de produits EOL dans le menu
const EOLBadge = ({ eolProducts }) => {
  const count = Array.isArray(eolProducts) ? eolProducts.length : 0;
  const recentCount = useMemo(() => {
    if (!Array.isArray(eolProducts) || eolProducts.length === 0) return 0;

    const today = new Date();
    return eolProducts.filter(product => {
      if (!product.terminated_at) return false;
      const terminatedDate = new Date(product.terminated_at);
      const daysSince = Math.floor((today - terminatedDate) / (1000 * 60 * 60 * 24));
      return daysSince <= 30;
    }).length;
  }, [eolProducts]);

  return (
    <>
      {count > 0 ? (
        <span className={`${recentCount > 0 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'} text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center`}>
          {count}
        </span>
      ) : (
        <span className="invisible text-xs px-2 py-0.5 min-w-[20px]">0</span>
      )}
    </>
  );
};

// Assigner le composant Badge à DNControlEOL.Badge
DNControlEOL.Badge = EOLBadge;

export default DNControlEOL;